import { DOWNLOADABLE_FILE_TYPES, VIEWABLE_FILE_TYPES } from '@exam-center/utils';
import {
  type BaseFileInputProps,
  type FormControlProps,
  FormControl,
  FormHelperText,
  BaseFileInput,
  Flex,
  FormLabel,
  DeleteOutlineIcon,
} from '@shared/components';
import { useCallback, useMemo } from 'react';
import { useController, type FieldValues, type UseControllerProps } from 'react-hook-form';

import { AttachedFilePreview } from '../attached-file-preview';

interface SignedUrlObject {
  url: string;
  name: string;
  type: string;
  size: number;
}

export type FormFilesInputProps<TFieldValues extends FieldValues = FieldValues> =
  UseControllerProps<TFieldValues> &
    FormControlProps &
    Omit<BaseFileInputProps, 'value' | 'onDrop'> & {
      label?: string;
      helperText?: string | React.ReactElement;
      onFilePreviewClick?: (file: File) => void;
      onDrop?: BaseFileInputProps['onDrop'];
    };

export default function FormFilesInput<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<FormFilesInputProps<TFieldValues>>,
) {
  const {
    disabled: isDisabled,
    helperText,
    label,
    name,
    onDrop,
    onReject,
    placeholder,
    required: isRequired,
    multiple: isMultiple,
    maxFiles,
    ...restProps
  } = props;

  const {
    field: { onChange, onBlur, ref, value, ...field },
    fieldState: { error },
  } = useController<TFieldValues>({ ...props, name });

  // REMARK: Disable input when file count reach maxFiles
  const shouldDisabled = isDisabled || (!!maxFiles && value?.length == maxFiles);

  const trimToMaxFiles = useCallback(
    (files: File[]) => {
      if (maxFiles) {
        return files.slice(0, maxFiles);
      }
      return files;
    },
    [maxFiles],
  );

  const handleDrop: BaseFileInputProps['onDrop'] = (acceptedFiles) => {
    onChange(trimToMaxFiles([...(value ?? []), ...acceptedFiles]));
    onDrop?.(acceptedFiles);
  };

  const handleRemove = useCallback(
    (index: number) => {
      const updatedFiles = (value ?? []).filter((_, fileIndex) => fileIndex !== index);
      onChange(updatedFiles);
    },
    [value, onChange],
  );

  const getFileMode = useCallback((file: File | SignedUrlObject) => {
    const fileType = file.type;
    if (DOWNLOADABLE_FILE_TYPES.includes(fileType)) {
      return 'download' as const;
    }
    if (VIEWABLE_FILE_TYPES.includes(fileType)) {
      return 'preview' as const;
    }
  }, []);

  const fileLabels = useMemo(
    () =>
      (value ?? []).map((file: File | SignedUrlObject, index) => ({
        name: file.name,
        value: file instanceof File ? file : { url: file.url, name: file.name },
        mode: getFileMode(file),
        endAdornment: (
          <DeleteOutlineIcon onClick={() => handleRemove(index)} sx={{ cursor: 'pointer' }} />
        ),
      })),
    [getFileMode, handleRemove, value],
  );

  const handleFileReject = useCallback<NonNullable<BaseFileInputProps['onReject']>>(
    (fileRejection) => {
      onChange(trimToMaxFiles([...(value ?? []), ...fileRejection.map((file) => file.file)]));
      onReject?.(fileRejection);
      onBlur();
    },
    [onReject, onBlur, value, onChange, trimToMaxFiles],
  );

  return (
    <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
      <FormControl
        sx={(theme) => ({ gap: theme.spacing(1) })}
        error={!!error}
        disabled={shouldDisabled}
        required={isRequired}
      >
        <FormLabel required={isRequired}>{label}</FormLabel>
        <BaseFileInput
          {...field}
          {...restProps}
          name={name}
          onDrop={handleDrop}
          onReject={handleFileReject}
          disabled={shouldDisabled}
          value={value}
          ref={ref}
          error={!!error}
          multiple={isMultiple}
          placeholder={placeholder}
          maxFiles={maxFiles}
        />
        {error?.message || helperText ? (
          <FormHelperText error={!!error}>
            {error?.message ? error.message : helperText}
          </FormHelperText>
        ) : null}
      </FormControl>
      {fileLabels.length > 0 ? <AttachedFilePreview files={fileLabels} /> : null}
    </Flex>
  );
}
